/* iceberg-ui */
import {
    Flex,
    IconWithVariant,
    LoadingSpinner,
    SpinnerSize
} from '@auviknetworks/iceberg-ui';
import { Box, Button, Typography } from '@mui/material';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DarkTooltip } from '../../Tooltips';
import { useManagedTableV2Context } from '../ManagedTableV2ContextProvider';

interface RefreshButtonProps {
    onClick: (() => void) | undefined;
    disableRefreshButton: boolean | undefined;
}

/**
 * Button component that handles refresh functionality
 * @param {(() => void) | undefined} props.onClick - Callback function when refresh button is clicked
 * @param {boolean | undefined} props.disableRefreshButton - Whether the refresh button should be disabled
 * @returns {JSX.Element} Rendered refresh button component
 */
const RefreshButton = ({
    onClick,
    disableRefreshButton
}: RefreshButtonProps): JSX.Element => {
    const { t } = useTranslation();
    const { tableName } = useManagedTableV2Context();
    return (
        <Button
            data-testid={`managedTableRefreshBtn_${tableName}`}
            data-trackid={`managedTableRefreshBtn_${tableName}`}
            disabled={disableRefreshButton}
            size="slim"
            onClick={onClick}
            variant="outlined"
        >
            {t('Common.refresh')}
        </Button>
    );
};

interface ManagedTableNewDataV2Props {
    customMessage?: React.ReactNode;
    disableRefreshButton?: boolean;
    newDataAvailable?: boolean;
    newDataLoading?: boolean;
    onRefresh?: () => void;
    refreshTooltip?: string;
}

/**
 * Component that displays new data availability status and refresh controls for managed tables
 * @param {React.ReactNode} [props.customMessage] - Optional custom message to display instead of default new data status
 * @param {boolean} [props.disableRefreshButton] - Whether the refresh button should be disabled
 * @param {boolean} [props.newDataAvailable] - Whether new data is available to load
 * @param {boolean} [props.newDataLoading] - Whether new data is currently being loaded
 * @param {() => void} [props.onRefresh] - Callback function when refresh button is clicked
 * @param {string} [props.refreshTooltip] - Tooltip text to show on refresh button hover
 * @returns {JSX.Element} Rendered component
 */
export const ManagedTableNewDataV2 = ({
    customMessage,
    disableRefreshButton,
    newDataAvailable,
    newDataLoading,
    onRefresh,
    refreshTooltip
}: ManagedTableNewDataV2Props): JSX.Element => {
    const { t } = useTranslation();
    return (
        <Box
            pt="9px"
            pr="16px"
            display="flex"
            marginLeft="auto"
            justifyContent="flex-end"
        >
            {(newDataAvailable || newDataLoading) && !customMessage && (
                <Flex sx={{ gap: '8px' }}>
                    {newDataAvailable && (
                        <IconWithVariant
                            variant="primary"
                            size={FontSizes[4]}
                            src="WarningTriangle"
                        />
                    )}
                    {newDataLoading && (
                        <LoadingSpinner size={SpinnerSize.BUTTON} />
                    )}
                    <Typography color="textPrimary">
                        {newDataAvailable &&
                            t('ManagedTable.newData.available')}
                        {newDataLoading && t('ManagedTable.newData.loading')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        {newDataAvailable && (
                            <RefreshButton
                                disableRefreshButton={disableRefreshButton}
                                onClick={onRefresh}
                            />
                        )}
                    </Box>
                </Flex>
            )}

            {customMessage && (
                <Flex alignItems="center">
                    <Box mr="16px">{customMessage}</Box>
                    {onRefresh && (
                        <DarkTooltip
                            arrow
                            title={refreshTooltip}
                            disableHoverListener={
                                !disableRefreshButton && !!refreshTooltip
                            }
                        >
                            <Box mr="4px">
                                <RefreshButton
                                    disableRefreshButton={disableRefreshButton}
                                    onClick={onRefresh}
                                />
                            </Box>
                        </DarkTooltip>
                    )}
                </Flex>
            )}
        </Box>
    );
};
